import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    Alert,
    Chip
} from '@mui/material';
import { ArrowLeft, Receipt, Info, CreditCard, Calendar, User, Clock, DollarSign } from 'lucide-react';
import { getRefundDetails } from '../../Services/Bookings/BookingsApis';

const getStatusChipSx = (status) => {
    switch (status?.toUpperCase()) {
        case 'COMPLETED':
        case 'APPROVED':
            return { bg: '#E8F5E9', color: '#2E7D32', border: '#C8E6C9' };
        case 'REQUESTED':
            return { bg: '#FFF3E0', color: '#E65100', border: '#FFE0B2' };
        case 'PROCESSING':
            return { bg: '#E3F2FD', color: '#1565C0', border: '#BBDEFB' };
        case 'REJECTED':
        case 'FAILED':
            return { bg: '#FFEBEE', color: '#C62828', border: '#FFCDD2' };
        default:
            return { bg: '#F5F5F5', color: '#616161', border: '#E0E0E0' };
    }
};

const DetailField = ({ label, value, chip = false, chipBg, chipColor, inline = false }) => {
    const displayValue = value || '-';

    if (inline) {
        return (
            <Box sx={{ mb: 1 }}>
                <Typography sx={{ fontSize: '0.875rem', color: '#1E293B', fontWeight: 400, wordBreak: 'break-word' }}>
                    <Typography component="span" sx={{ fontSize: '0.75rem', color: '#64748B', fontWeight: 500, mr: 0.5 }}>
                        {label}:
                    </Typography>
                    {displayValue}
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ mb: 1.5 }}>
            <Typography sx={{ fontSize: '0.75rem', color: '#64748B', fontWeight: 500, mb: 0.4 }}>
                {label}
            </Typography>
            {chip ? (
                <Chip
                    label={displayValue}
                    size="small"
                    sx={{
                        bgcolor: chipBg || getStatusChipSx(displayValue).bg,
                        color: chipColor || getStatusChipSx(displayValue).color,
                        border: `1px solid ${chipBg ? chipBg : getStatusChipSx(displayValue).border}`,
                        fontWeight: 500,
                        fontSize: '0.7rem',
                        borderRadius: '4px',
                        height: 22
                    }}
                />
            ) : (
                <Typography sx={{ fontSize: '0.875rem', color: '#1E293B', fontWeight: 400, wordBreak: 'break-word' }}>
                    {displayValue}
                </Typography>
            )}
        </Box>
    );
};

const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });
};

const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const RefundRequestDetails = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [refund, setRefund] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchRefundDetails = async () => {
            try {
                const refundId = location.state?.refundId;
                if (!refundId) {
                    throw new Error('Refund ID is missing');
                }

                // Fetch from list API
                const response = await getRefundDetails({ page: 1, limit: 25 });
                if (response && response.success) {
                    const refundData = response.data.find(r => r.id === refundId || r.refundId === refundId);
                    if (refundData) {
                        setRefund(refundData);
                    } else {
                        throw new Error('Refund not found');
                    }
                } else if (Array.isArray(response)) {
                    const refundData = response.find(r => r.id === refundId || r.refundId === refundId);
                    if (refundData) {
                        setRefund(refundData);
                    } else {
                        throw new Error('Refund not found');
                    }
                }
            } catch (err) {
                console.error('Error fetching refund details:', err);
                setError(err.message || 'Failed to fetch refund details');
            } finally {
                setLoading(false);
            }
        };

        fetchRefundDetails();
    }, [location.state]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', bgcolor: '#FAF8F5' }}>
                <CircularProgress sx={{ color: '#6E8964' }} />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3, bgcolor: '#FAF8F5', minHeight: '100vh' }}>
                <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
                <Button onClick={() => navigate('/bookings/refund-requests')} variant="outlined">
                    Back to Refund Requests
                </Button>
            </Box>
        );
    }

    if (!refund) {
        return (
            <Box sx={{ p: 3, bgcolor: '#FAF8F5', minHeight: '100vh' }}>
                <Alert severity="info">No refund data available</Alert>
                <Button onClick={() => navigate('/bookings/refund-requests')} variant="outlined" sx={{ mt: 2 }}>
                    Back to Refund Requests
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%', p: { xs: 2, sm: 2.5, md: 3 }, bgcolor: '#FAF8F5', minHeight: '100vh', boxSizing: 'border-box' }}>
            {/* Back Button */}
            <Button
                onClick={() => navigate('/bookings/refund-requests')}
                startIcon={<ArrowLeft size={16} />}
                disableElevation
                disableRipple
                sx={{
                    textTransform: 'none',
                    fontSize: '0.82rem',
                    fontWeight: 500,
                    color: '#64748B',
                    px: 0,
                    py: 0.5,
                    mb: 1.5,
                    '&:hover': {
                        bgcolor: 'transparent',
                        color: '#1E293B',
                    }
                }}
            >
                Back to Refund Requests
            </Button>

            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography sx={{ fontWeight: 600, color: '#1E293B', fontSize: '1.3rem', lineHeight: 1.2 }}>
                        Refund Request Details
                    </Typography>
                    <Typography sx={{ fontSize: '0.85rem', color: '#64748B', mt: 0.3 }}>
                        Complete information for the selected refund request
                    </Typography>
                </Box>
                <Chip
                    label={refund.status || 'Pending'}
                    size="small"
                    sx={{
                        ...getStatusChipSx(refund.status),
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        borderRadius: '4px',
                        height: 26
                    }}
                />
            </Box>

            {/* White Card Container */}
            <Box sx={{ bgcolor: '#FFFFFF', borderRadius: '8px', border: '1px solid #E0E0E0', p: 3, mb: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                {/* Summary Cards Row */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 0, mb: 3, border: '1px solid #E0E0E0', borderRadius: '8px', overflow: 'hidden', bgcolor: '#F5F5F5' }}>
                {/* Card 1 */}
                <Box sx={{ p: 2, borderRight: { xs: 'none', sm: '1px solid #E0E0E0' }, borderBottom: { xs: '1px solid #E0E0E0', sm: 'none' }, bgcolor: '#FFFFFF' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                        <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: '#F5F5F5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Receipt size={16} color="#64748B" />
                        </Box>
                        <Typography sx={{ fontSize: '0.7rem', color: '#64748B', fontWeight: 500, textTransform: 'uppercase' }}>
                            Refund Request ID
                        </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: '#1E293B' }}>
                        {refund.refundId || 'N/A'}
                    </Typography>
                </Box>

                {/* Card 2 */}
                <Box sx={{ p: 2, borderRight: { xs: 'none', md: '1px solid #E0E0E0' }, borderBottom: { xs: '1px solid #E0E0E0', sm: 'none' }, bgcolor: '#FFFFFF' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                        <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: '#F5F5F5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Calendar size={16} color="#64748B" />
                        </Box>
                        <Typography sx={{ fontSize: '0.7rem', color: '#64748B', fontWeight: 500, textTransform: 'uppercase' }}>
                            Request Date
                        </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: '#1E293B' }}>
                        {formatDate(refund.createdAt)}
                    </Typography>
                </Box>

                {/* Card 3 */}
                <Box sx={{ p: 2, borderRight: { xs: 'none', md: '1px solid #E0E0E0' }, borderBottom: { xs: '1px solid #E0E0E0', md: 'none' }, bgcolor: '#FFFFFF' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                        <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: '#F5F5F5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <DollarSign size={16} color="#64748B" />
                        </Box>
                        <Typography sx={{ fontSize: '0.7rem', color: '#64748B', fontWeight: 500, textTransform: 'uppercase' }}>
                            Requested Amount
                        </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: '#2E7D32' }}>
                        {refund.currency || 'INR'} {refund.refundAmount || '0.00'}
                    </Typography>
                </Box>

                {/* Card 4 */}
                <Box sx={{ p: 2, bgcolor: '#FFFFFF' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                        <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: '#F5F5F5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Clock size={16} color="#64748B" />
                        </Box>
                        <Typography sx={{ fontSize: '0.7rem', color: '#64748B', fontWeight: 500, textTransform: 'uppercase' }}>
                            Refund Status
                        </Typography>
                    </Box>
                    <Chip
                        label={refund.status || 'N/A'}
                        size="small"
                        sx={{
                            ...getStatusChipSx(refund.status),
                            fontWeight: 600,
                            fontSize: '0.72rem',
                            borderRadius: '4px',
                            height: 24
                        }}
                    />
                </Box>
            </Box>

            {/* Refund Information Section */}
            <Box sx={{ mb: 3, border: '1px solid #E0E0E0', borderRadius: '8px', overflow: 'hidden' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5, bgcolor: '#E3F2FD', borderBottom: '1px solid #BBDEFB' }}>
                    <Info size={18} color="#1565C0" />
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#1565C0' }}>
                        Refund Information
                    </Typography>
                </Box>
                <Box sx={{ p: 2 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 0 }}>
                        {/* Left Column */}
                        <Box sx={{ pr: { md: 2 }, borderRight: { xs: 'none', md: '1px solid #E0E0E0' } }}>
                            <DetailField label="Refund Request ID" value={refund.refundId} inline={true} />
                            <DetailField label="Requested Amount" value={`${refund.currency || 'INR'} ${refund.refundAmount || '0.00'}`} inline={true} />
                            <DetailField label="Refund Reason" value={refund.cancellationReason} inline={true} />
                            <DetailField label="Refund Status" value={refund.status} chip={true} />
                            <DetailField label="Request Date" value={formatDateTime(refund.createdAt)} inline={true} />
                        </Box>
                        {/* Right Column */}
                        <Box sx={{ pl: { md: 2 } }}>
                            <DetailField label="Refund Type" value={refund.refundPercentage ? `${refund.refundPercentage}% Refund` : 'Full Refund'} inline={true} />
                            <DetailField label="Cancellation Fee" value={`${refund.currency || 'INR'} ${refund.cancellationFee || '0.00'}`} inline={true} />
                            <DetailField label="Admin Remarks" value={refund.adminRemarks} inline={true} />
                            <DetailField label="Processed Date" value={formatDateTime(refund.processedAt)} inline={true} />
                            <DetailField label="Policy Details" value={refund.policyDetails} inline={true} />
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* Payment & Transaction Details Section */}
            <Box sx={{ mb: 3, border: '1px solid #E0E0E0', borderRadius: '8px', overflow: 'hidden' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5, bgcolor: '#E8F5E9', borderBottom: '1px solid #C8E6C9' }}>
                    <CreditCard size={18} color="#2E7D32" />
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#2E7D32' }}>
                        Payment & Transaction Details
                    </Typography>
                </Box>
                <Box sx={{ p: 2 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 0 }}>
                        {/* Left Column */}
                        <Box sx={{ pr: { md: 2 }, borderRight: { xs: 'none', md: '1px solid #E0E0E0' } }}>
                            <DetailField label="Transaction ID" value={refund.originalPaymentId || '-'} inline={true} />
                            <DetailField label="Payment Gateway" value={refund.paymentGateway || '-'} inline={true} />
                            <DetailField label="Total Booking Amount" value={`${refund.currency || 'INR'} ${refund.totalBookingAmount || '0.00'}`} inline={true} />
                            <DetailField label="Refund Transaction ID" value={refund.gatewayRefundId || '-'} inline={true} />
                        </Box>
                        {/* Right Column */}
                        <Box sx={{ pl: { md: 2 } }}>
                            <DetailField label="Request Date" value={formatDate(refund.createdAt)} inline={true} />
                            <DetailField label="Cancelled By" value={refund.cancelledBy || '-'} inline={true} />
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* Booking Information Section */}
            <Box sx={{ mb: 3, border: '1px solid #E0E0E0', borderRadius: '8px', overflow: 'hidden' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5, bgcolor: '#F3E5F5', borderBottom: '1px solid #E1BEE7' }}>
                    <Calendar size={18} color="#9C27B0" />
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#9C27B0' }}>
                        Booking Information
                    </Typography>
                </Box>
                <Box sx={{ p: 2 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(5, 1fr)' }, gap: 0 }}>
                        {/* Column 1 */}
                        <Box sx={{ pr: { md: 1.5 }, borderRight: { xs: 'none', md: '1px solid #E0E0E0' } }}>
                            <DetailField label="Booking ID" value={refund.booking?.bookingId || `#${refund.bookingId}`} inline={true} />
                        </Box>
                        {/* Column 2 */}
                        <Box sx={{ px: { md: 1.5 }, borderRight: { xs: 'none', md: '1px solid #E0E0E0' } }}>
                            <DetailField label="Booking Status" value={refund.booking?.bookingStatus || '-'} chip={true} chipBg="#FFEBEE" chipColor="#C62828" />
                        </Box>
                        {/* Column 3 */}
                        <Box sx={{ px: { md: 1.5 }, borderRight: { xs: 'none', md: '1px solid #E0E0E0' } }}>
                            <DetailField label="User ID" value={refund.userId || '-'} inline={true} />
                        </Box>
                        {/* Column 4 */}
                        <Box sx={{ px: { md: 1.5 }, borderRight: { xs: 'none', md: '1px solid #E0E0E0' } }}>
                            <DetailField label="Refund ID" value={refund.refundId || '-'} inline={true} />
                        </Box>
                        {/* Column 5 */}
                        <Box sx={{ pl: { md: 1.5 } }}>
                            <DetailField label="Status" value={refund.status || '-'} chip={true} />
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* User Information Section */}
            <Box sx={{ mb: 3, border: '1px solid #E0E0E0', borderRadius: '8px', overflow: 'hidden' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5, bgcolor: '#FCE4EC', borderBottom: '1px solid #F8BBD9' }}>
                    <User size={18} color="#D81B60" />
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#D81B60' }}>
                        User Information
                    </Typography>
                </Box>
                <Box sx={{ p: 2 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(5, 1fr)' }, gap: 0 }}>
                        {/* Column 1 */}
                        <Box sx={{ pr: { md: 1.5 }, borderRight: { xs: 'none', md: '1px solid #E0E0E0' } }}>
                            <DetailField label="User ID" value={refund.userId || '-'} inline={true} />
                        </Box>
                        {/* Column 2 */}
                        <Box sx={{ px: { md: 1.5 }, borderRight: { xs: 'none', md: '1px solid #E0E0E0' } }}>
                            <DetailField label="Created By" value={refund.cuName || '-'} inline={true} />
                        </Box>
                        {/* Column 3 */}
                        <Box sx={{ px: { md: 1.5 }, borderRight: { xs: 'none', md: '1px solid #E0E0E0' } }}>
                            <DetailField label="Updated By" value={refund.uuName || '-'} inline={true} />
                        </Box>
                        {/* Column 4 */}
                        <Box sx={{ px: { md: 1.5 }, borderRight: { xs: 'none', md: '1px solid #E0E0E0' } }}>
                            <DetailField label="Created User Type" value={refund.cuType || '-'} inline={true} />
                        </Box>
                        {/* Column 5 */}
                        <Box sx={{ pl: { md: 1.5 } }}>
                            <DetailField label="Updated User Type" value={refund.uuType || '-'} inline={true} />
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* Audit Information Section */}
            <Box sx={{ mb: 2, border: '1px solid #E0E0E0', borderRadius: '8px', overflow: 'hidden' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5, bgcolor: '#F5F5F5', borderBottom: '1px solid #E0E0E0' }}>
                    <Clock size={18} color="#616161" />
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#616161' }}>
                        Audit Information
                    </Typography>
                </Box>
                <Box sx={{ p: 2 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 0 }}>
                        {/* Left Column */}
                        <Box sx={{ pr: { md: 2 }, borderRight: { xs: 'none', md: '1px solid #E0E0E0' } }}>
                            <DetailField label="Created By" value={refund.cuName || '-'} inline={true} />
                            <DetailField label="Created User Type" value={refund.cuType || '-'} inline={true} />
                            <DetailField label="Created At" value={formatDateTime(refund.createdAt)} inline={true} />
                        </Box>
                        {/* Right Column */}
                        <Box sx={{ pl: { md: 2 } }}>
                            <DetailField label="Updated By" value={refund.uuName || '-'} inline={true} />
                            <DetailField label="Updated User Type" value={refund.uuType || '-'} inline={true} />
                            <DetailField label="Updated At" value={formatDateTime(refund.updatedAt)} inline={true} />
                        </Box>
                    </Box>
                </Box>
            </Box>
            </Box>
        </Box>
    );
};

export default RefundRequestDetails;
